import React, { useState, useEffect, Suspense, lazy } from "react";
import { func, array } from "prop-types";
import { useSelector } from "react-redux";
import get from "lodash/get";

import Checkbox from "../../../atoms/Checkbox";

import "../../../../../assets/stylesheets/search-page.scss";
import "./search-form.m.css";
import { staticTexts } from "../../../utils/translate";

const DatePicker = lazy(() => import("react-date-picker/dist/entry.nostyle"));

const Filter = ({ sections, setparams }) => {
  const lang = useSelector((state) => get(state, ["qt", "config", "publisher-attributes", "lang"], ""));
  const [isSections, setIsSections] = useState(false);
  const [sectionIdList, setsectionIdList] = useState([]);
  const [firstTime, setFirstTime] = useState(true);
  const [queryParams, setQueryParams] = useState(null);

  const [isAuthors, setIsAuthors] = useState(false);
  const [authorsList, setAuthorsList] = useState([]);
  const [authorIdList, setAuthorIdList] = useState([]);

  const [isStoryTemplate, setIsStoryTemplate] = useState(false);
  const [templateList, setTemplateList] = useState([]);

  const [isDateViewOpen, setIsDateViewOpen] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const setAuthors = async () => {
    const { page } = await (await fetch("/api/v1/authors?limit=1")).json();

    if (page && page.total) {
      const { authors } = await (await fetch(`/api/v1/authors?limit=${page.total}`)).json();
      setAuthorsList(authors);
    }
  };

  useEffect(() => {
    setAuthors();
  }, []);

  const sectionChange = (e, sectionId) => {
    let idList = null;

    if (e.target.checked) {
      idList = [...sectionIdList];
      idList.push(sectionId);
    } else {
      idList = sectionIdList.filter((id) => id !== sectionId);
    }

    firstTime && setFirstTime(false);
    setsectionIdList(idList);
  };

  const authorChange = (e, authorId) => {
    let idList = null;

    if (e.target.checked) {
      idList = [...authorIdList];
      idList.push(authorId);
    } else {
      idList = authorIdList.filter((id) => id !== authorId);
    }

    firstTime && setFirstTime(false);
    setAuthorIdList(idList);
  };

  const templateChange = (e, template) => {
    let idList = null;

    if (e.target.checked) {
      idList = [...templateList];
      idList.push(template);
    } else {
      idList = templateList.filter((id) => id !== template);
    }

    firstTime && setFirstTime(false);
    setTemplateList(idList);
  };

  const getStoryList = () => {
    const params = {};

    if (authorIdList && authorIdList.length) {
      params["author-id"] = authorIdList.join();
    }

    if (sectionIdList && sectionIdList.length) {
      params["section-id"] = sectionIdList.join();
    }

    if (templateList && templateList.length) {
      params.type = templateList.join();
    }

    if (fromDate) {
      params["published-after"] = fromDate.valueOf();
    }

    if (toDate) {
      params["published-before"] = toDate.valueOf();
    }

    let apiFields = "";

    if (Object.keys(params).length) {
      Object.keys(params).map((item, index) => {
        if (params[item]) {
          apiFields += `${index > 0 ? "&" : ""}${Object.keys(params)[index]}=${params[item]}`;
        }
        return null;
      });
      setQueryParams(apiFields);
    } else {
      setQueryParams(null);
    }

    setparams(apiFields);
  };

  const clear = () => {
    setAuthorIdList([]);
    setsectionIdList([]);
    setTemplateList([]);
    setFromDate(null);
    setToDate(null);
  };

  useEffect(() => {
    getStoryList();
  }, [authorIdList, sectionIdList, templateList, fromDate, toDate]);

  const getDateView = () => (
    <div>
      <Suspense fallback={<div />}>
        <DatePicker onChange={setFromDate} value={fromDate} />
      </Suspense>
      <span styleName="date-seperator">{staticTexts(lang).to}</span>
      <Suspense fallback={<div />}>
        <DatePicker onChange={setToDate} value={toDate} />
      </Suspense>
    </div>
  );

  const dateLabelClick = async () => {
    setIsDateViewOpen(!isDateViewOpen);
  };

  return (
    <div styleName="filter-wrapper" data-test-id="filter-options-wrapper">
      <div styleName="filter">
        {staticTexts(lang).filter}
        {queryParams && (
          <div styleName="clear-button" onClick={clear}>
            {staticTexts(lang).clear}
          </div>
        )}
      </div>
      <ul data-test-id="filter-option">
        <li styleName="filter-option-wrapper" data-test-id="section-filter">
          <div
            styleName={`filter-option ${isSections ? "open-sort" : ""}`}
            onClick={() => {
              setIsSections(!isSections);
            }}
          >
            {staticTexts(lang).section}
          </div>
          {isSections && sections.length > 0 && (
            <ul styleName="filter-dropdown">
              {sections.map((section, index) => (
                <li styleName="filter-name" key={index}>
                  <Checkbox
                    text={section.name}
                    isChecked={sectionIdList.includes(section.id)}
                    onChange={(e) => sectionChange(e, section.id)}
                  />
                </li>
              ))}
            </ul>
          )}
        </li>
        <li styleName="filter-option-wrapper" data-test-id="author-filter">
          <div
            styleName={`filter-option ${isAuthors ? "open-sort" : ""}`}
            onClick={() => {
              setIsAuthors(!isAuthors);
            }}
          >
            {staticTexts(lang).author}
          </div>
          {isAuthors && authorsList.length > 0 && (
            <ul styleName="filter-dropdown">
              {authorsList.map((author, index) => (
                <li styleName="filter-name" key={index}>
                  <Checkbox
                    text={author.name}
                    isChecked={authorIdList.includes(author.id)}
                    onChange={(e) => authorChange(e, author.id)}
                  />
                </li>
              ))}
            </ul>
          )}
        </li>
        <li styleName="filter-option-wrapper" data-test-id="template-filter">
          <div
            styleName={`filter-option ${isStoryTemplate ? "open-sort" : ""}`}
            onClick={() => {
              setIsStoryTemplate(!isStoryTemplate);
            }}
          >
            {staticTexts(lang).storyType}
          </div>
          {isStoryTemplate && staticTexts(lang).templateOptions.length > 0 && (
            <ul styleName="filter-dropdown">
              {staticTexts(lang).templateOptions.map((template, index) => (
                <li styleName="filter-name" key={index}>
                  <Checkbox
                    text={template}
                    isChecked={templateList.includes(template)}
                    onChange={(e) => templateChange(e, template)}
                  />
                </li>
              ))}
            </ul>
          )}
        </li>
        <li styleName="filter-option-wrapper" data-test-id="date-filter">
          <div styleName={`filter-option ${isDateViewOpen ? "open-sort" : ""}`} onClick={dateLabelClick}>
            {staticTexts(lang).date}
          </div>
          {isDateViewOpen ? getDateView() : null}
        </li>
      </ul>
    </div>
  );
};

Filter.propTypes = {
  sections: array,
  setparams: func,
};

export default Filter;
